import  { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Switch,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Paper,
  Chip,
 
  InputAdornment,
  FormGroup,
  Divider,
  Container
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Email as EmailIcon,
  Notifications as BellIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon
} from '@mui/icons-material';


const baseColors = {
  primary: {
    main: '#EB5A3C',       
    light: '#FF7A5C',      
    dark: '#C54428',        
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F8EEDB',        
    light: '#FFFAF0',     
    dark: '#E6D4B7',        
    contrastText: '#333333',
  },
  background: {
    default: '#F8EEDB',     
    paper: '#FFFFFF',
  },
  text: {
    primary: '#2C3E50',     
    secondary: '#5D6D7E',   
  },
  grey: {
    50: '#F8F9FA',
    100: '#E9ECEF',
    200: '#DEE2E6',
    300: '#CED4DA',
    400: '#ADB5BD',
    500: '#6C757D',
    600: '#495057',
    700: '#343A40',
    800: '#212529',
    900: '#0D1117',
  },
  success: {
    main: '#27AE60',
    light: '#2ECC71',
    dark: '#229954',
  },
  warning: {
    main: '#F39C12',
    light: '#F4D03F',
    dark: '#D68910',
  },
  error: {
    main: '#E74C3C',
    light: '#EC7063',
    dark: '#C0392B',
  },
  info: {
    main: '#3498DB',
    light: '#5DADE2',
    dark: '#2980B9',
  },
};

const theme = createTheme({
  palette: {
    mode: 'light',
    ...baseColors,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Segoe UI", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.75rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.005em',
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#5D6D7E',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 2px 12px rgba(235, 90, 60, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(0,0,0,0.05)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(235, 90, 60, 0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 600,
          padding: '8px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#FFFAF0',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#F8EEDB',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
            },
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const NotificationsPage = () => {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [emailAddress, setEmailAddress] = useState("");
  const [smsNumber, setSmsNumber] = useState("");

  const [emailPreferences, setEmailPreferences] = useState({
    accountChanges: true,
    groupChanges: true,
    productUpdates: true,
    newProducts: true,
    marketing: true,
    security: true,
  });

  const [pushPreferences, setPushPreferences] = useState({
    comments: true,
    shares: true,
    follows: true,
    messages: true,
    security: true,
  });

  const [generalPreferences, setGeneralPreferences] = useState({
    autoSubscribeGroups: true,
    autoSubscribeProducts: true,
  });

  const handleEmailPreferenceChange = (key) => {
    setEmailPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handlePushPreferenceChange = (key) => {
    setPushPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleGeneralPreferenceChange = (key) => {
    setGeneralPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const CustomCheckbox = ({ checked, onChange, label, description }) => (
    <MotionBox
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 1,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'secondary.light',
            borderColor: 'primary.main',
          },
        }}
        onClick={onChange}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Checkbox
            checked={checked}
            color="primary"
            sx={{ mt: -1 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={500} color="text.primary">
              {label}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </MotionBox>
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: 'background.default',
        py: 4
      }}>
        <Container maxWidth="xl">
          <MotionBox
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Header */}
            <MotionBox variants={itemVariants} sx={{ mb: 4 }}>
              <Typography variant="h1" color="text.primary" gutterBottom>
                Notification Settings
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Manage how and when you receive notifications
              </Typography>
            </MotionBox>

            <Grid container spacing={3}>
              {/* Email Notifications */}
              <Grid item xs={12} lg={8}>
                <MotionCard
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardHeader
                    avatar={
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: 'primary.light', 
                        borderRadius: 2,
                        color: 'white'
                      }}>
                        <EmailIcon />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" color="primary.main">
                        Email Notifications
                      </Typography>
                    }
                    action={
                      <Switch
                        checked={emailEnabled}
                        onChange={() => setEmailEnabled(!emailEnabled)}
                        color="primary"
                      />
                    }
                    sx={{ 
                      backgroundColor: 'secondary.main',
                      borderBottom: 1,
                      borderColor: 'secondary.dark'
                    }}
                  />
                  <CardContent>
                    {emailEnabled ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Default notification email"
                          type="email"
                          value={emailAddress}
                          onChange={(e) => setEmailAddress(e.target.value)}
                          placeholder="name@example.com"
                          sx={{ mb: 3 }}
                        />

                        <Typography variant="h6" gutterBottom>
                          Choose which types of email updates you receive
                        </Typography>
                        
                        <FormGroup>
                          <CustomCheckbox
                            checked={emailPreferences.accountChanges}
                            onChange={() => handleEmailPreferenceChange("accountChanges")}
                            label="Changes made to your account"
                            description="Get notified when your account settings are modified"
                          />
                          <CustomCheckbox
                            checked={emailPreferences.groupChanges}
                            onChange={() => handleEmailPreferenceChange("groupChanges")}
                            label="Changes are made to groups you're part of"
                            description="Updates about groups you've joined"
                          />
                          <CustomCheckbox
                            checked={emailPreferences.productUpdates}
                            onChange={() => handleEmailPreferenceChange("productUpdates")}
                            label="Product updates for products you've purchased or starred"
                            description="New features and improvements for your products"
                          />
                          <CustomCheckbox
                            checked={emailPreferences.newProducts}
                            onChange={() => handleEmailPreferenceChange("newProducts")}
                            label="Information on new products and services"
                            description="Be the first to know about new offerings"
                          />
                          <CustomCheckbox
                            checked={emailPreferences.marketing}
                            onChange={() => handleEmailPreferenceChange("marketing")}
                            label="Marketing and promotional offers"
                            description="Special deals and promotional content"
                          />
                          <CustomCheckbox
                            checked={emailPreferences.security}
                            onChange={() => handleEmailPreferenceChange("security")}
                            label="Security alerts"
                            description="Important security notifications and warnings"
                          />
                        </FormGroup>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <EmailIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography color="text.secondary">
                          Email notifications are disabled
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </MotionCard>
              </Grid>

              {/* Notification Preferences Sidebar */}
              <Grid item xs={12} lg={4}>
                <MotionCard
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardHeader
                    avatar={
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: 'primary.light', 
                        borderRadius: 2,
                        color: 'white'
                      }}>
                        <SettingsIcon />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" color="primary.main">
                        Preferences
                      </Typography>
                    }
                    sx={{ 
                      backgroundColor: 'secondary.main',
                      borderBottom: 1,
                      borderColor: 'secondary.dark'
                    }}
                  />
                  <CardContent>
                    <FormGroup sx={{ mb: 3 }}>
                      <CustomCheckbox
                        checked={generalPreferences.autoSubscribeGroups}
                        onChange={() => handleGeneralPreferenceChange("autoSubscribeGroups")}
                        label="Auto-subscribe to group notifications"
                        description="Automatically receive notifications from new groups you join"
                      />
                      <CustomCheckbox
                        checked={generalPreferences.autoSubscribeProducts}
                        onChange={() => handleGeneralPreferenceChange("autoSubscribeProducts")}
                        label="Auto-subscribe to product notifications"
                        description="Get updates for new products automatically"
                      />
                    </FormGroup>

                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      sx={{ 
                        py: 1.5,
                        backgroundColor: 'error.light',
                        color: 'error.dark',
                        '&:hover': {
                          backgroundColor: 'error.main',
                          color: 'white'
                        }
                      }}
                    >
                      Unsubscribe from all notifications
                    </Button>
                  </CardContent>
                </MotionCard>
              </Grid>

              {/* Push Notifications */}
              <Grid item xs={12} lg={8}>
                <MotionCard
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardHeader
                    avatar={
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: 'primary.light', 
                        borderRadius: 2,
                        color: 'white'
                      }}>
                        <BellIcon />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" color="primary.main">
                        Push Notifications
                      </Typography>
                    }
                    action={
                      <Switch
                        checked={pushEnabled}
                        onChange={() => setPushEnabled(!pushEnabled)}
                        color="primary"
                      />
                    }
                    sx={{ 
                      backgroundColor: 'secondary.main',
                      borderBottom: 1,
                      borderColor: 'secondary.dark'
                    }}
                  />
                  <CardContent>
                    {pushEnabled ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Default SMS number"
                          type="tel"
                          value={smsNumber}
                          onChange={(e) => setSmsNumber(e.target.value)}
                          placeholder="123-456-7890"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 3 }}
                        />

                        <Typography variant="h6" gutterBottom>
                          Choose which types of push notifications you receive
                        </Typography>
                        
                        <FormGroup>
                          <CustomCheckbox
                            checked={pushPreferences.comments}
                            onChange={() => handlePushPreferenceChange("comments")}
                            label="Someone comments on your post"
                            description="Get notified when someone engages with your content"
                          />
                          <CustomCheckbox
                            checked={pushPreferences.shares}
                            onChange={() => handlePushPreferenceChange("shares")}
                            label="Someone shares your post"
                            description="Know when your content is being shared"
                          />
                          <CustomCheckbox
                            checked={pushPreferences.follows}
                            onChange={() => handlePushPreferenceChange("follows")}
                            label="A user follows your account"
                            description="Get notified about new followers"
                          />
                          <CustomCheckbox
                            checked={pushPreferences.messages}
                            onChange={() => handlePushPreferenceChange("messages")}
                            label="You receive a private message"
                            description="Instant notifications for direct messages"
                          />
                          <CustomCheckbox
                            checked={pushPreferences.security}
                            onChange={() => handlePushPreferenceChange("security")}
                            label="Security alerts"
                            description="Critical security notifications"
                          />
                        </FormGroup>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <BellIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography color="text.secondary">
                          Push notifications are disabled
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </MotionCard>
              </Grid>

              {/* Notification Summary */}
              <Grid item xs={12} lg={4}>
                <MotionCard
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardHeader
                    title={
                      <Typography variant="h6" color="primary.main">
                        Notification Summary
                      </Typography>
                    }
                    sx={{ 
                      backgroundColor: 'secondary.main',
                      borderBottom: 1,
                      borderColor: 'secondary.dark'
                    }}
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon color="primary" />
                          <Typography variant="body2" fontWeight={500}>
                            Email
                          </Typography>
                        </Box>
                        <Chip
                          label={emailEnabled ? "Enabled" : "Disabled"}
                          color={emailEnabled ? "success" : "default"}
                          size="small"
                        />
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          backgroundColor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BellIcon color="primary" />
                          <Typography variant="body2" fontWeight={500}>
                            Push
                          </Typography>
                        </Box>
                        <Chip
                          label={pushEnabled ? "Enabled" : "Disabled"}
                          color={pushEnabled ? "success" : "default"}
                          size="small"
                        />
                      </Paper>

                      {emailEnabled && (
                        <Box>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Active email notifications:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Object.values(emailPreferences).filter(Boolean).length} of{" "}
                            {Object.keys(emailPreferences).length} enabled
                          </Typography>
                        </Box>
                      )}

                      {pushEnabled && (
                        <Box>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Active push notifications:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Object.values(pushPreferences).filter(Boolean).length} of{" "}
                            {Object.keys(pushPreferences).length} enabled
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ py: 1.5 }}
                    >
                      Save All Settings
                    </Button>
                  </CardContent>
                </MotionCard>
              </Grid>
            </Grid>
          </MotionBox>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default NotificationsPage;